import { array } from 'fp-ts';
import { getSelectedHotelConfigurationId } from '../../modules/configuration/selectors';
import { getProductPricingHash } from '../../modules/entities/productPricing/getProductPricingHash';
import {
    fetchProductPricingFailed,
    fetchProductPricingStarted,
    fetchProductPricingSucceeded,
    setProductPricing,
} from '../../modules/entities/productPricing/reducer';
import { hasProductPricing } from '../../modules/entities/productPricing/selectors';
import { getEnterpriseAbsoluteProductIds } from '../../modules/entities/products/selectors';
import { getEnterpriseServiceId } from '../../modules/entities/services/selectors';
import { OccupancyData } from '../../modules/resourceOrder/occupancyTypes';
import { areAllOccupanciesZero } from '../../modules/resourceOrder/occupancyUtils';
import {
    getEndDate,
    getEndTimeUtc,
    getSelectedHotelId,
    getStartDate,
    getStartTimeUtc,
    getUniqueOccupanciesOccupancyData,
} from '../../modules/resourceOrder/selectors';
import { getSelectedEnterpriseTimezone } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { DistributorPromiseThunkAction } from '../../types';
import { WithCurrencyCode } from '../../types/selectors/selectorPropTypes';
import { toUtc } from '../../utils/dates';

interface FetchProductPricesOptions {
    configurationId: string;
    currencyCode: string;
    endDate: string;
    startDate: string;
    occupancyData: OccupancyData[];
    productIds: string[];
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const fetchProductPrices =
    ({
        occupancyData,
        configurationId,
        startDate,
        endDate,
        productIds,
        currencyCode,
        startTimeUtc,
        endTimeUtc,
    }: FetchProductPricesOptions): DistributorPromiseThunkAction =>
    async (dispatch, getState, { apiService }) => {
        const productPricingHash = getProductPricingHash({
            occupancyData,
            startDate: startTimeUtc ?? startDate,
            endDate: endTimeUtc ?? endDate,
            configurationId,
            currencyCode,
        });
        dispatch(fetchProductPricingStarted({ productPricingHash }));
        try {
            const timezone = getSelectedEnterpriseTimezone(getState(), {});
            if (timezone == null) {
                throw new TypeError('Selected enterprise timezone is nil');
            }
            const startUtc = toUtc(startDate, timezone);
            const endUtc = toUtc(endDate, timezone);

            const response = await apiService.getProductsPrices({
                occupancyData,
                configurationId,
                startUtc: startTimeUtc ?? startUtc,
                endUtc: endTimeUtc ?? endUtc,
                productIds,
                currencyCode,
            });
            const responseWithMeta = { prices: response.prices, productPricingHash };
            dispatch(setProductPricing(responseWithMeta));
            dispatch(fetchProductPricingSucceeded({ productPricingHash }));
        } catch (error) {
            dispatch(fetchProductPricingFailed({ productPricingHash }));
            throw error;
        }
    };

export const fetchCurrentDateAndOccupancyProductPricesWithCheck =
    ({ currencyCode }: WithCurrencyCode): DistributorPromiseThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);
        const productIds = getEnterpriseAbsoluteProductIds(state, { enterpriseId });
        const configurationId = getSelectedHotelConfigurationId(state, { enterpriseId });
        const startDate = getStartDate(state);
        const endDate = getEndDate(state);
        const startTimeUtc = getStartTimeUtc(state);
        const endTimeUtc = getEndTimeUtc(state);
        const occupancyData = getUniqueOccupanciesOccupancyData(state);
        const serviceId = getEnterpriseServiceId(state, { enterpriseId });
        if (startDate == null || endDate == null || configurationId == null || serviceId == null) {
            return;
        }

        if (array.isEmpty(occupancyData)) {
            return;
        }

        const productPricingHash = getProductPricingHash({
            occupancyData,
            configurationId,
            startDate: startTimeUtc ?? startDate,
            endDate: endTimeUtc ?? endDate,
            currencyCode,
        });

        if (hasProductPricing(state, { productPricingHash }) || areAllOccupanciesZero(occupancyData)) {
            return;
        }

        return dispatch(
            fetchProductPrices({
                productIds,
                configurationId,
                startDate,
                endDate,
                occupancyData,
                currencyCode,
                startTimeUtc,
                endTimeUtc,
            })
        );
    };
